import { SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useRef, useState, type FC } from "react";
import { Input } from "../ui/input";
import useDebounce from "@/hooks/useDebounce";
import type { RestaurantAutocompleteResult } from "@/API";
import Text from "../Text";
import {
  autoCompleteRestaurantState,
  searchForAutoCompleteRestaurant,
} from "@/store/autoCompleteRestaurantState";
import { useTranslation } from "react-i18next";

type Props = {
  onChange?: (value: RestaurantAutocompleteResult) => void;
};

const SearchRestaurant: FC<Props> = ({ onChange }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [value, setValue] = useState<RestaurantAutocompleteResult | null>(null);
  const [buttonWidth, setButtonWidth] = useState(500);

  const refButton = useRef<HTMLButtonElement>(null);

  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = autoCompleteRestaurantState();

  useEffect(() => {
    return () => {
      setOpen(false);
      const PopoverContentElement = document.getElementById(
        "search-restaurant-popover",
      );
      if (PopoverContentElement) {
        PopoverContentElement.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (debouncedSearch.length > 2) {
      searchForAutoCompleteRestaurant({ search });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (refButton.current) {
      setButtonWidth(refButton.current.clientWidth);
    }
  }, [refButton]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={refButton}
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className="w-[100%] px-[24px] py-[12px] bg-[white] justify-between rounded-[72px]"
        >
          <Text className="truncate">
            {value ? value.name : t("find_restaurant")}
          </Text>

          <SearchIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-[12px]"
        style={{ width: buttonWidth }}
        id="search-restaurant-popover"
      >
        <Input
          placeholder={t("research_restaurant")}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        {isLoading && (
          <div className="mt-[12px]">
            <Text>{t("searching_process")}</Text>
          </div>
        )}
        <div className="flex flex-col gap-[8px] mt-[8px] max-h-[400px] overflow-auto">
          {data &&
            data.map((restaurant, index) => (
              <button
                key={`auto-complete-${restaurant.id}-${index}`}
                onClick={() => {
                  setValue(restaurant);
                  onChange?.(restaurant);
                  setOpen(false);
                }}
                className="flex flex-col items-start px-[12px] py-[8px] hover:bg-[#F1F2F4]"
              >
                <Text fontSize={16} fontWeight={400}>
                  {restaurant.name}
                </Text>
                <Text fontSize={14} fontWeight={400}>
                  {restaurant.address}
                </Text>
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchRestaurant;
